import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import ClinicalSelectContent from './ClinicalSelectContent';
import ClinicalFormShell from './ClinicalFormShell';
import PathologyBlock from './PathologyBlock';
import MedicationList from './MedicationList';
import ClinicalAlertsBanner from './ClinicalAlertsBanner';
import VascularExplorationSection from './VascularExplorationSection';
import {
  ADULT_SYMPTOM_OPTIONS,
  BLOOD_TYPE_OPTIONS,
  CARDIOVASCULAR_CONDITIONS,
  CONSENT_LEGAL_TEXT,
  DERMATOLOGICAL_CONDITIONS,
  EDUCATION_OPTIONS,
  ENDOCRINE_CONDITIONS,
  EVOLUTION_UNITS,
  FOOT_HYGIENE_OPTIONS,
  FOOTWEAR_HABIT_OPTIONS,
  GASTROINTESTINAL_CONDITIONS,
  INTAKE_WIZARD_STEPS,
  LIMITATION_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  NEUROLOGICAL_CONDITIONS,
  PROGNOSIS_OPTIONS,
  REFLEX_OPTIONS,
  mergeIntakeAdult,
} from '../../lib/clinicalIntakeSchema';
import { patchPatientRecord, patchPatient } from '../../lib/api';
import { openConsentPreview } from '../../lib/consentDocument';
import { printIntakeRecord } from '../../lib/printIntakeRecord';
import { useClinicBranding } from '../../context/ClinicBrandingContext';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, FileText, Printer, Save } from 'lucide-react';

function LateralSelectRow({ label, leftVal, rightVal, onLeft, onRight }) {
  return (
    <tr className="border-b border-border/60">
      <td className="py-2 pr-2 text-sm font-medium">{label}</td>
      <td className="py-2 px-1">
        <Select value={leftVal || ''} onValueChange={onLeft}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Izq." />
          </SelectTrigger>
          <ClinicalSelectContent>
            {REFLEX_OPTIONS.map((o) => (
              <SelectItem key={`l-${o.value}`} value={o.value || 'none'}>
                {o.label}
              </SelectItem>
            ))}
          </ClinicalSelectContent>
        </Select>
      </td>
      <td className="py-2 pl-1">
        <Select value={rightVal || ''} onValueChange={onRight}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Der." />
          </SelectTrigger>
          <ClinicalSelectContent>
            {REFLEX_OPTIONS.map((o) => (
              <SelectItem key={`r-${o.value}`} value={o.value || 'none'}>
                {o.label}
              </SelectItem>
            ))}
          </ClinicalSelectContent>
        </Select>
      </td>
    </tr>
  );
}

export default function IntakeWizard({
  recordId,
  patientId,
  initialIntake,
  initialConsent,
  recordStatus,
  onSaved,
  onCompleted,
}) {
  const readOnly = recordStatus === 'complete';
  const { info: clinicInfo } = useClinicBranding();
  const [step, setStep] = useState(0);
  const [intake, setIntake] = useState(() => mergeIntakeAdult(initialIntake));
  const [consent, setConsent] = useState(() => ({ ...(initialConsent || {}) }));
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    setIntake(mergeIntakeAdult(initialIntake));
    setConsent({ ...(initialConsent || {}) });
  }, [initialIntake, initialConsent]);

  const updateIntake = useCallback((path, value) => {
    setIntake((prev) => {
      const next = mergeIntakeAdult(prev);
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  const saveDraft = async (markComplete = false) => {
    if (readOnly && !markComplete) {
      toast.message('La historia está completa; use encuentros para notas de evolución.');
      return;
    }
    if (markComplete && !String(intake.personal_extended?.chief_complaint || '').trim()) {
      toast.error('Indique el motivo principal de consulta');
      setStep(INTAKE_WIZARD_STEPS.findIndex((s) => s.id === 'personal_ext'));
      return;
    }
    if (markComplete && !String(intake.diagnosis_treatment?.diagnosis_primary || '').trim()) {
      toast.error('Indique el diagnóstico principal');
      setStep(INTAKE_WIZARD_STEPS.findIndex((s) => s.id === 'diagnosis'));
      return;
    }
    setSaving(true);
    try {
      const personal = intake.personal || {};
      const addr = [personal.address_line1, personal.address_line2].filter(Boolean).join(', ');
      if (patientId && personal.full_name) {
        await patchPatient(patientId, {
          full_name: personal.full_name,
          birth_date: personal.birth_date,
          sex: personal.biological_sex || personal.sex,
          phone: personal.phone,
          email: personal.email,
          address: addr || personal.address,
          emergency_contact:
            personal.emergency_contact ||
            [personal.emergency_contact_name, personal.emergency_contact_phone]
              .filter(Boolean)
              .join(' · '),
          occupation: personal.occupation,
        });
      }
      const consentPayload =
        markComplete && !consent.signed_at
          ? { ...consent, signed_at: new Date().toISOString().slice(0, 10) }
          : consent;
      const payload = {
        intake_adult: {
          ...intake,
          diagnosis_treatment: {
            ...intake.diagnosis_treatment,
            diagnosis:
              intake.diagnosis_treatment?.diagnosis_primary ||
              intake.diagnosis_treatment?.diagnosis ||
              '',
          },
        },
        consent: consentPayload,
        status: markComplete ? 'complete' : recordStatus === 'complete' ? 'complete' : 'draft',
      };
      if (markComplete && consent?.authorized) {
        payload.status = 'complete';
      }
      const saved = await patchPatientRecord(recordId, payload);
      toast.success(markComplete ? 'Historia clínica guardada' : 'Borrador guardado');
      onSaved?.(saved);
      if (markComplete) {
        onCompleted?.(saved);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const stepId = INTAKE_WIZARD_STEPS[step]?.id;

  const handlePrintConsent = () => {
    const patientName =
      String(consent.patient_signature_name || '').trim() ||
      String(intake.personal?.full_name || '').trim();
    if (!patientName) {
      toast.error('Indique el nombre del paciente para imprimir el consentimiento');
      return;
    }
    const payload = {
      clinicName: 'PodoclinicAM',
      patientName,
      signedAt: String(consent.signed_at || '').trim(),
      podiatristName: String(consent.podiatrist_name || '').trim(),
      podiatristLicense: String(consent.podiatrist_license || '').trim(),
      clinicPlace: String(intake.header?.clinic_address || '').trim(),
      folio: String(intake.header?.folio || '').trim(),
    };
    const opened = openConsentPreview(payload);
    if (!opened?.ok) {
      toast.error('Permite ventanas emergentes para abrir el consentimiento');
    }
  };

  const handlePrintFullRecord = async () => {
    setPrinting(true);
    try {
      const result = await printIntakeRecord({ intake, consent, clinicInfo });
      if (!result?.ok) {
        toast.error('Permite ventanas emergentes para abrir la historia clínica');
      }
    } catch {
      toast.error('No se pudo preparar la impresión');
    } finally {
      setPrinting(false);
    }
  };

  const renderStep = () => {
    if (stepId === 'personal') {
      const p = intake.personal;
      const allergies = p.allergies || { has_allergies: false, text: '' };
      const tp = p.treating_physician || { name: '', specialty: '', phone: '' };
      return (
        <div className="space-y-4">
          <ClinicalAlertsBanner intake={intake} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Folio</Label>
                <Input className="mt-1 h-11" disabled value={intake.header?.folio || ''} readOnly />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  className="mt-1 h-11"
                  disabled={readOnly}
                  value={intake.header?.form_date || ''}
                  onChange={(e) => updateIntake('header', { ...intake.header, form_date: e.target.value })}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Nombre completo *</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.full_name}
                onChange={(e) => updateIntake('personal', { ...p, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Fecha de nacimiento</Label>
              <Input
                type="date"
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.birth_date}
                onChange={(e) => updateIntake('personal', { ...p, birth_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Sexo biológico *</Label>
              <Select
                value={p.biological_sex || p.sex || ''}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('personal', { ...p, biological_sex: v, sex: v })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <ClinicalSelectContent>
                  <SelectItem value="femenino">Femenino</SelectItem>
                  <SelectItem value="masculino">Masculino</SelectItem>
                  <SelectItem value="otro">Otro / prefiero no decir</SelectItem>
                </ClinicalSelectContent>
              </Select>
            </div>
            <div>
              <Label>Género (opcional)</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.gender || ''}
                onChange={(e) => updateIntake('personal', { ...p, gender: e.target.value })}
              />
            </div>
            <div>
              <Label>Estado civil</Label>
              <Select
                value={p.marital_status || ''}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('personal', { ...p, marital_status: v === 'none' ? '' : v })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <ClinicalSelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {MARITAL_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </ClinicalSelectContent>
              </Select>
            </div>
            <div>
              <Label>Escolaridad</Label>
              <Select
                value={p.education || ''}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('personal', { ...p, education: v === 'none' ? '' : v })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <ClinicalSelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {EDUCATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </ClinicalSelectContent>
              </Select>
            </div>
            <div>
              <Label>CURP</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                maxLength={18}
                value={p.curp || ''}
                onChange={(e) => updateIntake('personal', { ...p, curp: e.target.value })}
              />
            </div>
            <div>
              <Label>Tipo de sangre</Label>
              <Select
                value={p.blood_type || ''}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('personal', { ...p, blood_type: v === 'none' ? '' : v })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <ClinicalSelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {BLOOD_TYPE_OPTIONS.map((bt) => (
                    <SelectItem key={bt} value={bt}>
                      {bt}
                    </SelectItem>
                  ))}
                </ClinicalSelectContent>
              </Select>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.phone}
                onChange={(e) => updateIntake('personal', { ...p, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Correo</Label>
              <Input
                type="email"
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.email}
                onChange={(e) => updateIntake('personal', { ...p, email: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Domicilio (línea 1)</Label>
              <Textarea
                className="mt-1 min-h-[4rem]"
                disabled={readOnly}
                value={p.address_line1 || ''}
                onChange={(e) => updateIntake('personal', { ...p, address_line1: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Domicilio (línea 2)</Label>
              <Textarea
                className="mt-1 min-h-[4rem]"
                disabled={readOnly}
                value={p.address_line2 || ''}
                onChange={(e) => updateIntake('personal', { ...p, address_line2: e.target.value })}
              />
            </div>
            <div>
              <Label>Emergencia — nombre</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.emergency_contact_name || ''}
                onChange={(e) =>
                  updateIntake('personal', { ...p, emergency_contact_name: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Emergencia — teléfono</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.emergency_contact_phone || ''}
                onChange={(e) =>
                  updateIntake('personal', { ...p, emergency_contact_phone: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Parentesco</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.emergency_contact_relationship || ''}
                onChange={(e) =>
                  updateIntake('personal', {
                    ...p,
                    emergency_contact_relationship: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Ocupación</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={p.occupation}
                onChange={(e) => updateIntake('personal', { ...p, occupation: e.target.value })}
              />
            </div>
          </div>
          <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
            <label className="flex items-center gap-2 font-medium">
              <Checkbox
                disabled={readOnly}
                checked={!!allergies.has_allergies}
                onCheckedChange={(c) =>
                  updateIntake('personal', {
                    ...p,
                    allergies: { ...allergies, has_allergies: Boolean(c) },
                  })
                }
              />
              ¿Alergias conocidas?
            </label>
            {allergies.has_allergies ? (
              <Textarea
                disabled={readOnly}
                placeholder="Especifique alergias y reacciones"
                value={allergies.text || ''}
                onChange={(e) =>
                  updateIntake('personal', {
                    ...p,
                    allergies: { ...allergies, text: e.target.value },
                  })
                }
              />
            ) : null}
          </div>
          <MedicationList
            items={p.current_medications}
            disabled={readOnly}
            onChange={(list) => updateIntake('personal', { ...p, current_medications: list })}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Médico tratante</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={tp.name}
                onChange={(e) =>
                  updateIntake('personal', {
                    ...p,
                    treating_physician: { ...tp, name: e.target.value },
                  })
                }
              />
            </div>
            <div>
              <Label>Especialidad</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={tp.specialty}
                onChange={(e) =>
                  updateIntake('personal', {
                    ...p,
                    treating_physician: { ...tp, specialty: e.target.value },
                  })
                }
              />
            </div>
            <div>
              <Label>Tel. médico</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={tp.phone}
                onChange={(e) =>
                  updateIntake('personal', {
                    ...p,
                    treating_physician: { ...tp, phone: e.target.value },
                  })
                }
              />
            </div>
          </div>
        </div>
      );
    }

    if (stepId === 'antecedents') {
      const np = intake.non_pathological;
      const toggleFootwear = (id) => {
        const list = np.footwear_habits || [];
        const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
        updateIntake('non_pathological', { ...np, footwear_habits: next });
      };
      return (
        <div className="space-y-6">
          <ClinicalAlertsBanner intake={intake} />
          <div>
            <h3 className="font-semibold mb-3">Antecedentes patológicos</h3>
            <div className="grid grid-cols-1 gap-3">
              <PathologyBlock
                label="Cardiovasculares"
                value={intake.pathological.cardiovascular}
                conditionOptions={CARDIOVASCULAR_CONDITIONS}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, cardiovascular: v })
                }
              />
              <PathologyBlock
                label="Pulmonares"
                value={intake.pathological.pulmonary}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, pulmonary: v })
                }
              />
              <PathologyBlock
                label="Gastrointestinales"
                value={intake.pathological.gastrointestinal}
                conditionOptions={GASTROINTESTINAL_CONDITIONS}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, gastrointestinal: v })
                }
              />
              <PathologyBlock
                label="Endocrinos"
                value={intake.pathological.endocrine}
                conditionOptions={ENDOCRINE_CONDITIONS}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, endocrine: v })
                }
              />
              <PathologyBlock
                label="Dermatológicos"
                value={intake.pathological.dermatological}
                conditionOptions={DERMATOLOGICAL_CONDITIONS}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, dermatological: v })
                }
              />
              <PathologyBlock
                label="Neurológicos"
                value={intake.pathological.neurological}
                conditionOptions={NEUROLOGICAL_CONDITIONS}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, neurological: v })
                }
              />
              <PathologyBlock
                label="Cáncer"
                value={intake.pathological.cancer}
                onChange={(v) =>
                  updateIntake('pathological', { ...intake.pathological, cancer: v })
                }
              />
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-3">Antecedentes no patológicos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ['smoking', 'Tabaquismo'],
                ['alcoholism', 'Alcoholismo'],
                ['substance_use', 'Toxicomanías'],
                ['trauma', 'Traumatismos'],
                ['surgeries', 'Cirugías'],
              ].map(([k, label]) => (
                <PathologyBlock
                  key={k}
                  label={label}
                  value={np[k]}
                  onChange={(v) => updateIntake('non_pathological', { ...np, [k]: v })}
                />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Actividad física</Label>
                <Input
                  className="mt-1 h-11"
                  disabled={readOnly}
                  value={np.physical_activity || ''}
                  onChange={(e) =>
                    updateIntake('non_pathological', { ...np, physical_activity: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Higiene podal</Label>
                <Select
                  value={np.foot_hygiene || ''}
                  disabled={readOnly}
                  onValueChange={(v) =>
                    updateIntake('non_pathological', {
                      ...np,
                      foot_hygiene: v === 'none' ? '' : v,
                    })
                  }
                >
                  <SelectTrigger className="mt-1 h-11">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <ClinicalSelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {FOOT_HYGIENE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </ClinicalSelectContent>
                </Select>
              </div>
            </div>
            <Label className="mt-3 block">Calzado habitual</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FOOTWEAR_HABIT_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-1.5 text-sm border rounded-md px-2 py-1 cursor-pointer"
                >
                  <Checkbox
                    disabled={readOnly}
                    checked={(np.footwear_habits || []).includes(opt.id)}
                    onCheckedChange={() => toggleFootwear(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-3">Antecedentes heredofamiliares</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ['diabetes', 'Diabetes'],
                ['hypertension', 'Hipertensión'],
                ['peripheral_vascular', 'Enf. vascular periférica'],
                ['amputations', 'Amputaciones'],
                ['neuropathies', 'Neuropatías'],
                ['rheumatologic', 'Reumatológicos'],
                ['cancer', 'Cáncer'],
                ['seizures', 'Crisis convulsivas'],
              ].map(([k, label]) => (
                <PathologyBlock
                  key={k}
                  label={label}
                  value={intake.family_history[k]}
                  onChange={(v) =>
                    updateIntake('family_history', { ...intake.family_history, [k]: v })
                  }
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (stepId === 'personal_ext') {
      const pe = intake.personal_extended;
      const symptoms = pe.symptoms || {};
      const toggleSymptom = (key) => {
        updateIntake('personal_extended', {
          ...pe,
          symptoms: { ...symptoms, [key]: !symptoms[key] },
        });
      };
      return (
        <div className="space-y-4">
          <div>
            <Label>Motivo principal de consulta *</Label>
            <Textarea
              className="mt-1 min-h-[6rem]"
              disabled={readOnly}
              value={pe.chief_complaint || ''}
              onChange={(e) =>
                updateIntake('personal_extended', { ...pe, chief_complaint: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Dolor (EVA 0–10)</Label>
              <Input
                type="number"
                min={0}
                max={10}
                className="mt-1 h-11"
                disabled={readOnly}
                value={pe.pain_eva ?? ''}
                onChange={(e) =>
                  updateIntake('personal_extended', { ...pe, pain_eva: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Tiempo de evolución</Label>
              <Input
                type="number"
                min={0}
                className="mt-1 h-11"
                disabled={readOnly}
                value={pe.evolution_value ?? ''}
                onChange={(e) =>
                  updateIntake('personal_extended', { ...pe, evolution_value: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Unidad</Label>
              <Select
                value={pe.evolution_unit || 'meses'}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('personal_extended', { ...pe, evolution_unit: v })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue />
                </SelectTrigger>
                <ClinicalSelectContent>
                  {EVOLUTION_UNITS.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label}
                    </SelectItem>
                  ))}
                </ClinicalSelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Síntomas actuales</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ADULT_SYMPTOM_OPTIONS.map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 cursor-pointer rounded-lg border p-2"
                >
                  <Checkbox
                    disabled={readOnly}
                    checked={!!symptoms[key]}
                    onCheckedChange={() => toggleSymptom(key)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Terapias o tratamientos previos</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={pe.prior_therapies || ''}
              onChange={(e) =>
                updateIntake('personal_extended', { ...pe, prior_therapies: e.target.value })
              }
            />
          </div>
          <div>
            <Label>Laboratorios / estudios (resumen)</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={pe.lab_tests_done || ''}
              onChange={(e) =>
                updateIntake('personal_extended', { ...pe, lab_tests_done: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fecha del estudio</Label>
              <Input
                type="date"
                className="mt-1 h-11"
                disabled={readOnly}
                value={pe.lab_tests_when || ''}
                onChange={(e) =>
                  updateIntake('personal_extended', { ...pe, lab_tests_when: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Resultados</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={pe.lab_tests_results || ''}
                onChange={(e) =>
                  updateIntake('personal_extended', { ...pe, lab_tests_results: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      );
    }

    if (stepId === 'podiatric') {
      const po = intake.podiatric;
      return (
        <div className="space-y-6">
          <VascularExplorationSection
            value={po}
            readOnly={readOnly}
            recordId={recordId}
            onChange={(next) => updateIntake('podiatric', next)}
          />
          <div>
            <h3 className="text-base font-semibold text-foreground mb-2">Reflejos</h3>
            <table className="w-full text-sm border rounded-lg overflow-hidden">
              <thead className="bg-muted/40">
                <tr className="text-muted-foreground">
                  <th className="text-left p-2">Reflejo</th>
                  <th className="p-2">Izquierdo</th>
                  <th className="p-2">Derecho</th>
                </tr>
              </thead>
              <tbody>
                <LateralSelectRow
                  label="Aquíleo"
                  leftVal={po.reflexes?.left?.achilles === 'none' ? '' : po.reflexes?.left?.achilles}
                  rightVal={po.reflexes?.right?.achilles === 'none' ? '' : po.reflexes?.right?.achilles}
                  onLeft={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        left: { ...po.reflexes.left, achilles: v === 'none' ? '' : v },
                      },
                    })
                  }
                  onRight={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        right: { ...po.reflexes.right, achilles: v === 'none' ? '' : v },
                      },
                    })
                  }
                />
                <LateralSelectRow
                  label="Rotuliano"
                  leftVal={po.reflexes?.left?.patellar}
                  rightVal={po.reflexes?.right?.patellar}
                  onLeft={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        left: { ...po.reflexes.left, patellar: v === 'none' ? '' : v },
                      },
                    })
                  }
                  onRight={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        right: { ...po.reflexes.right, patellar: v === 'none' ? '' : v },
                      },
                    })
                  }
                />
                <LateralSelectRow
                  label="Plantar"
                  leftVal={po.reflexes?.left?.plantar}
                  rightVal={po.reflexes?.right?.plantar}
                  onLeft={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        left: { ...po.reflexes.left, plantar: v === 'none' ? '' : v },
                      },
                    })
                  }
                  onRight={(v) =>
                    updateIntake('podiatric', {
                      ...po,
                      reflexes: {
                        ...po.reflexes,
                        right: { ...po.reflexes.right, plantar: v === 'none' ? '' : v },
                      },
                    })
                  }
                />
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (stepId === 'biomechanical') {
      const bio = intake.biomechanical;
      const segKeys = [
        ['hip', 'Cadera'],
        ['knee', 'Rodilla'],
        ['hindfoot', 'Retropie'],
        ['forefoot', 'Antepie'],
        ['midfoot', 'Medio pie'],
        ['ankle', 'Tobillo'],
      ];
      return (
        <div className="space-y-4">
          <div>
            <Label>Limitación funcional</Label>
            <Select value={bio.limitation || ''} onValueChange={(v) => updateIntake('biomechanical', { ...bio, limitation: v })}>
              <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <ClinicalSelectContent>
                {LIMITATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value || 'x'} value={o.value || 'none'}>{o.label}</SelectItem>
                ))}
              </ClinicalSelectContent>
            </Select>
          </div>
          <div>
            <Label>Columna</Label>
            <Textarea className="mt-1" rows={2} value={bio.column_spine || ''} onChange={(e) => updateIntake('biomechanical', { ...bio, column_spine: e.target.value })} />
          </div>
          <table className="w-full text-sm border rounded-lg overflow-hidden">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2 text-left">Segmento</th>
                <th className="p-2">Derecho</th>
                <th className="p-2">Izquierdo</th>
              </tr>
            </thead>
            <tbody>
              {segKeys.map(([k, label]) => (
                <tr key={k} className="border-t">
                  <td className="p-2 font-medium">{label}</td>
                  <td className="p-1">
                    <Input
                      value={bio.segments?.right?.[k] || ''}
                      onChange={(e) =>
                        updateIntake('biomechanical', {
                          ...bio,
                          segments: { ...bio.segments, right: { ...bio.segments.right, [k]: e.target.value } },
                        })
                      }
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      value={bio.segments?.left?.[k] || ''}
                      onChange={(e) =>
                        updateIntake('biomechanical', {
                          ...bio,
                          segments: { ...bio.segments, left: { ...bio.segments.left, [k]: e.target.value } },
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (stepId === 'muscle') {
      const md = intake.muscle_dynamic;
      return (
        <div className="space-y-4">
          <div>
            <Label>Exploración muscular (escala Davis / MRC)</Label>
            <Textarea className="mt-1" rows={3} value={md.muscle_exam} onChange={(e) => updateIntake('muscle_dynamic', { ...md, muscle_exam: e.target.value })} />
          </div>
          <div>
            <Label>Exploración dinámica</Label>
            <Textarea className="mt-1" rows={3} value={md.dynamic_exam} onChange={(e) => updateIntake('muscle_dynamic', { ...md, dynamic_exam: e.target.value })} />
          </div>
          <div>
            <Label>Tipo de calzado</Label>
            <Input className="mt-1 h-11" value={md.footwear} onChange={(e) => updateIntake('muscle_dynamic', { ...md, footwear: e.target.value })} />
          </div>
        </div>
      );
    }

    if (stepId === 'diagnosis') {
      const dt = intake.diagnosis_treatment;
      const secondary = Array.isArray(dt.diagnosis_secondary) ? dt.diagnosis_secondary : [];
      return (
        <div className="space-y-4">
          <div>
            <Label>Diagnóstico principal *</Label>
            <Textarea
              className="mt-1"
              rows={3}
              disabled={readOnly}
              value={dt.diagnosis_primary || dt.diagnosis || ''}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', {
                  ...dt,
                  diagnosis_primary: e.target.value,
                  diagnosis: e.target.value,
                })
              }
            />
          </div>
          <div>
            <Label>Diagnósticos secundarios (uno por línea)</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={secondary.join('\n')}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', {
                  ...dt,
                  diagnosis_secondary: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>CIE-10 (opcional)</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={dt.cie10 || ''}
                onChange={(e) =>
                  updateIntake('diagnosis_treatment', { ...dt, cie10: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Pronóstico</Label>
              <Select
                value={dt.prognosis || ''}
                disabled={readOnly}
                onValueChange={(v) =>
                  updateIntake('diagnosis_treatment', {
                    ...dt,
                    prognosis: v === 'none' ? '' : v,
                  })
                }
              >
                <SelectTrigger className="mt-1 h-11">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <ClinicalSelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {PROGNOSIS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </ClinicalSelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Procedimiento realizado hoy</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={dt.procedure_today || ''}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', { ...dt, procedure_today: e.target.value })
              }
            />
          </div>
          <div>
            <Label>Plan de tratamiento</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={dt.treatment_plan || dt.orthotic_treatment || ''}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', {
                  ...dt,
                  treatment_plan: e.target.value,
                  orthotic_treatment: e.target.value,
                })
              }
            />
          </div>
          <div>
            <Label>Indicaciones post-tratamiento</Label>
            <Textarea
              className="mt-1"
              rows={2}
              disabled={readOnly}
              value={dt.post_care_instructions || ''}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', {
                  ...dt,
                  post_care_instructions: e.target.value,
                })
              }
            />
          </div>
          <div>
            <Label>Próxima cita</Label>
            <Input
              type="date"
              className="mt-1 h-11"
              disabled={readOnly}
              value={dt.next_appointment || ''}
              onChange={(e) =>
                updateIntake('diagnosis_treatment', { ...dt, next_appointment: e.target.value })
              }
            />
          </div>
        </div>
      );
    }

    if (stepId === 'consent') {
      return (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed whitespace-pre-wrap border rounded-lg p-4 bg-muted/30 max-h-48 overflow-y-auto">
            {CONSENT_LEGAL_TEXT}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nombre del paciente (firma)</Label>
              <Input className="mt-1 h-11" value={consent.patient_signature_name || ''} onChange={(e) => setConsent({ ...consent, patient_signature_name: e.target.value })} />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" className="mt-1 h-11" value={consent.signed_at || ''} onChange={(e) => setConsent({ ...consent, signed_at: e.target.value })} />
            </div>
            <div>
              <Label>Responsable de atención podológica</Label>
              <Input
                className="mt-1 h-11"
                disabled={readOnly}
                value={consent.podiatrist_name || ''}
                onChange={(e) => setConsent({ ...consent, podiatrist_name: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2 rounded-lg border p-4">
            <p className="text-sm font-medium">Fotografías clínicas del pie</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                disabled={readOnly}
                checked={consent.clinical_photos_consent === 'yes'}
                onCheckedChange={() =>
                  setConsent({ ...consent, clinical_photos_consent: 'yes' })
                }
              />
              <span className="text-sm">Autorizo fotografías con fines clínicos y de expediente</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                disabled={readOnly}
                checked={consent.clinical_photos_consent === 'no'}
                onCheckedChange={() =>
                  setConsent({ ...consent, clinical_photos_consent: 'no' })
                }
              />
              <span className="text-sm">No autorizo fotografías clínicas</span>
            </label>
          </div>
          <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-4 bg-muted/30">
            <Checkbox
              disabled={readOnly}
              checked={!!consent.authorized}
              onCheckedChange={(c) => setConsent({ ...consent, authorized: Boolean(c) })}
            />
            <span className="text-sm">He leído la información y autorizo la práctica podológica descrita.</span>
          </label>
        </div>
      );
    }

    return null;
  };

  return (
    <ClinicalFormShell
      title="Historia clínica (adulto)"
      subtitle="Complete por secciones; puede guardar borrador en cualquier momento."
      stepIndex={step}
      stepCount={INTAKE_WIZARD_STEPS.length}
      stepTitle={INTAKE_WIZARD_STEPS[step]?.title}
      footer={
        <>
          <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>
          <Button type="button" variant="secondary" disabled={saving || readOnly} onClick={() => saveDraft(false)}>
            <Save className="w-4 h-4 mr-1" />
            Guardar borrador
          </Button>
          <Button type="button" variant="outline" disabled={printing} onClick={handlePrintFullRecord}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimir historia clínica
          </Button>
          {step < INTAKE_WIZARD_STEPS.length - 1 ? (
            <Button type="button" onClick={() => setStep((s) => s + 1)}>
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={handlePrintConsent}>
                <FileText className="w-4 h-4 mr-1" />
                Ver / Imprimir consentimiento
              </Button>
              <Button type="button" disabled={saving || readOnly || !consent.authorized} onClick={() => saveDraft(true)}>
                Finalizar historia
              </Button>
            </>
          )}
        </>
      }
    >
      {readOnly ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Historia marcada como completa. Los cambios deben registrarse en encuentros de seguimiento.
        </p>
      ) : null}
      {renderStep()}
    </ClinicalFormShell>
  );
}
